// Справка „Фактури" (Справки → invoices).
//
// Списък на издадените фактури за периода. `GET /invoices` връща целия период
// наведнъж (масив, без страниране), затова споделената таблица работи в
// КЛИЕНТСКИ режим: сортирането, филтрите в колоните и редът „Общо" се смятат в
// браузъра.
//
// Кликът върху ред отваря `InvoicePreviewModal` — СЪЩИЯТ модал, който показва
// фактурата в „Маркиране". Той е по нормативно задание и се печата, затова се
// ползва както е и с точно същите пропсове: оттам вървят преглед, печат (на
// хартия и на POS, като ДУБЛИКАТ), редакция на получателя и анулиране заедно с
// потвърждението и съобщенията си.
//
// Правото (`view_invoices`) се пази от `AuthorizedRoute` в `main.tsx`; тук
// остава втората линия — 403 от списъка или от детайла се съобщава на човека,
// вместо екранът да мълчи.

import { useEffect, useMemo, useState } from "react";
import { App, Spin } from "antd";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import { useInvoiceById, useInvoicesList } from "../../../hooks/useInvoices";
import { InvoicePreviewModal } from "../../ordering/components/tables/invoicing/InvoicePreviewModal";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { pluralLabel } from "./reportPlural";
import { InvoicesKpis } from "./invoices/InvoicesKpis";
import { InvoiceMobileCard } from "./invoices/InvoiceMobileCard";
import { useInvoicesColumns } from "./invoices/invoicesColumns";
import { exportInvoices } from "./invoices/invoicesExport";
import {
  buildInvoiceRows,
  summarizeInvoices,
  type InvoiceRow,
} from "./invoices/invoicesMath";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const InvoicesReportPage = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const { message } = App.useApp();

  // Датата живее и в `?date=` — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [searchText, setSearchText] = useState("");
  // Редовете СЛЕД филтър и подредба — Excel изнася видяното, не суровия набор.
  const [visibleRows, setVisibleRows] = useState<InvoiceRow[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // `GET /invoices` филтрира по ДЕН (`YYYY-MM-DD`), не по момент — часът не се
  // праща и справката няма избор на час.
  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      date_from: dateRange[0].format("YYYY-MM-DD"),
      date_to: dateRange[1].format("YYYY-MM-DD"),
    };
  }, [dateRange]);

  // Предходният равен по дължина отрязък — само за Δ чиповете. Тече ли още
  // избраният период, краят се подрязва до изтеклото (виж `reportPeriod`).
  const previousFilters = useMemo(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    const previous = previousPeriodOf(
      dateRange[0].startOf("day"),
      dateRange[1].endOf("day"),
    );
    return {
      date_from: previous.start.format("YYYY-MM-DD"),
      date_to: previous.end.format("YYYY-MM-DD"),
    };
  }, [compare, dateRange]);

  const {
    data: invoices = [],
    isLoading,
    isFetching,
    refetch,
    error: listError,
  } = useInvoicesList(filters);

  // При изключено сравнение се пита за СЪЩИЯ период — React Query го дава от
  // кеша по същия ключ, без втора мрежова заявка.
  const { data: previousInvoices = [] } = useInvoicesList(
    previousFilters ?? filters,
  );

  const {
    data: selectedInvoice,
    isFetching: isLoadingDetail,
    error: detailError,
  } = useInvoiceById(selectedInvoiceId);

  useEffect(() => {
    const hasForbidden = [listError, detailError].some(
      (error) => isAxiosError(error) && error.response?.status === 403,
    );
    if (hasForbidden) message.error(t("common.forbidden"));
  }, [listError, detailError, message, t]);

  // Детайлът се дърпа по id; модалът се отваря чак когато фактурата е тук —
  // иначе за миг се вижда празен документ.
  useEffect(() => {
    if (selectedInvoice && selectedInvoiceId) setIsPreviewOpen(true);
  }, [selectedInvoice, selectedInvoiceId]);

  // Етикетите се разрешават В САМИТЕ редове, не в `accessorFn` — иначе
  // TanStack ги заковава на езика от първия рендер (виж `invoicesMath`).
  const labels = useMemo(
    () => ({
      tax: t("invoicesReport.typeTax"),
      simplified: t("invoicesReport.typeSimplified"),
      issued: t("reportsPage.invoicesStatusIssued"),
      cancelled: t("reportsPage.invoicesStatusCancelled"),
    }),
    [t],
  );

  const rows = useMemo(
    () => buildInvoiceRows(invoices, labels),
    [invoices, labels],
  );
  const previousRows = useMemo(
    () => (previousFilters ? buildInvoiceRows(previousInvoices, labels) : []),
    [previousFilters, previousInvoices, labels],
  );

  // Локално филтриране по търсачката — BE-то поддържа и `q`, но целият период
  // вече е в браузъра и клиентското търсене отговаря, докато човек пише.
  // Гледа точно това, което се чете в реда: номер, получател, ЕИК.
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      `${row.number} ${row.customerName} ${row.customerEik}`
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, searchText]);

  const summary = useMemo(() => summarizeInvoices(rows), [rows]);
  const previousSummary = useMemo(
    () => (previousFilters ? summarizeInvoices(previousRows) : null),
    [previousFilters, previousRows],
  );

  const columns = useInvoicesColumns(labels);

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.invoices")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "invoicesReport.resultOne",
          "invoicesReport.results",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.invoicesTableHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("invoicesReport.title")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <InvoicesKpis
                summary={summary}
                previous={previousSummary}
                isNarrow={narrow}
              />

              <DataTable<InvoiceRow>
                tableId="invoices"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => String(row.id)}
                refreshing={isFetching || isLoadingDetail}
                ariaLabel={t("invoicesReport.title")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("invoicesReport.searchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportInvoices(visibleRows, t)}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                mobileCard={(row) => <InvoiceMobileCard row={row} />}
                onRowClick={(row) => setSelectedInvoiceId(row.id)}
                // Анулираната бие избраната: тонът ѝ е трайното състояние на
                // документа, а изборът трае, докато модалът е отворен върху
                // екрана.
                rowTone={(row) =>
                  row.isCancelled
                    ? "danger"
                    : row.id === selectedInvoiceId
                      ? "brand"
                      : null
                }
                initialSorting={[{ id: "issueDate", desc: true }]}
                emptyTitle={t("invoicesReport.empty")}
                emptyHint={t("reportsPage.invoicesEmptyHint")}
              />
            </>
          )}
        </div>
      </div>

      {/* Документът е по нормативно задание и се печата — ползва се както е. */}
      <InvoicePreviewModal
        open={isPreviewOpen}
        invoice={selectedInvoice ?? null}
        allowCancel
        allowEdit
        posPrintMode="duplicate"
        onClose={() => {
          setIsPreviewOpen(false);
          setSelectedInvoiceId(null);
        }}
      />
    </div>
  );
};

export default InvoicesReportPage;
